import type { ListingsModel, PadType } from '@padspin/firestore-listings';
import {
  listingSeoSlug,
  slugInputsChanged,
  type ListingSeoInput,
} from './listing-seo-slug';

/** The canonical origin for public listing URLs. */
export const LISTING_URL_ORIGIN = 'https://padspin.com';

/** The first path segment of a listing URL, by property type. */
export const LISTING_PATH_PREFIX_BY_TYPE = {
  apartment: 'no-broker-fee-apartments-for-rent',
  house: 'no-broker-fee-house-for-rent',
  condo: 'no-broker-fee-condo-for-rent',
  townhouse: 'no-broker-fee-townhouse-for-rent',
  'co-op': 'no-broker-fee-co-op-for-rent',
} as const satisfies Record<PadType, string>;

/** The prefix for a listing whose property type is unknown. */
export const DEFAULT_LISTING_PATH_PREFIX =
  LISTING_PATH_PREFIX_BY_TYPE.apartment;

/** Every first path segment a canonical listing URL can start with. */
export const LISTING_PATH_PREFIXES = Object.values(LISTING_PATH_PREFIX_BY_TYPE);

/**
 * First path segments that served listing pages before the property-type
 * prefixes. Listing URLs under them still open the listing.
 */
export const LEGACY_LISTING_PATH_PREFIXES = [
  'l',
  'listings',
  'no-fee-apartments-for-rent',
] as const;

/** Listing fields needed to build a listing path or URL. */
export type ListingUrlInput = Pick<ListingsModel, 'slug'> &
  Partial<Pick<ListingsModel, 'slugFriendly' | 'type'> & ListingSeoInput>;

/**
 * The first path segment for a listing of `type`. Unknown or missing types
 * get the apartment prefix.
 */
export function listingPathPrefix(type: unknown): string {
  return Object.hasOwn(LISTING_PATH_PREFIX_BY_TYPE, type as PropertyKey)
    ? LISTING_PATH_PREFIX_BY_TYPE[type as PadType]
    : DEFAULT_LISTING_PATH_PREFIX;
}

/**
 * Returns `/<prefix>/<slug>/<suffix>`, where the prefix comes from the
 * listing's property type. Uses the stored `slugFriendly` when it is
 * non-empty and derives the suffix otherwise. Returns `/<prefix>/<slug>` when
 * no suffix can be derived.
 */
export function listingPath(listing: ListingUrlInput): string {
  const base = `/${listingPathPrefix(listing.type)}/${listing.slug}`;
  // Legacy stored values were free text typed by admins (spaces, `&`, `/`).
  const suffix = listing.slugFriendly
    ? encodeURIComponent(listing.slugFriendly)
    : listingSeoSlug(listing);
  return suffix ? `${base}/${suffix}` : base;
}

/**
 * Returns the path of a listing sub-page, `/<prefix>/<slug>/<subPath>`, e.g.
 * `listingSubPath(listing, 'editor', 'details')`.
 */
export function listingSubPath(
  listing: Pick<ListingUrlInput, 'slug' | 'type'>,
  ...subPath: string[]
): string {
  return ['', listingPathPrefix(listing.type), listing.slug, ...subPath].join(
    '/',
  );
}

/** Returns the canonical URL, `https://padspin.com/<prefix>/<slug>/<suffix>`. */
export function listingUrl(listing: ListingUrlInput): string {
  return `${LISTING_URL_ORIGIN}${listingPath(listing)}`;
}

/**
 * True when a field the listing's canonical URL is derived from (the suffix
 * inputs or the property type) differs between `before` and `after`.
 */
export function listingUrlInputsChanged(
  before: Partial<ListingSeoInput & Pick<ListingsModel, 'type'>>,
  after: Partial<ListingSeoInput & Pick<ListingsModel, 'type'>>,
): boolean {
  return slugInputsChanged(before, after) || before.type !== after.type;
}
